import { convertedQtyFormatterConfig } from '@/config/config';
import { mergeRefs, parseShortFormNumber } from '@/utils/common';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import useLanguage from '@/utils/hooks/useLanguage';
import BigNumber from 'bignumber.js';
import cn from 'classnames';
import { ComponentProps, forwardRef, useEffect, useRef } from 'react';
import { useLocale, useNumberField } from 'react-aria';
import { useFormContext } from 'react-hook-form';
import { useNumberFieldState } from 'react-stately';
import PortalCurrencyIcon from '../common/PortalCurrencyIcon';

// to be deprecated

interface Props extends ComponentProps<'input'> {
    name: string;
    label?: string;
    min?: string | number;
    step?: number;
    className?: any;
    disabled?: boolean;
    currency?: string;
}

const RHFNumberInput = forwardRef<HTMLInputElement, Props>((props, ref) => {
    const { name, label, min = 0, step = 1, disabled, className, currency } = props;

    const { getFieldState, getValues, formState, setValue, watch } = useFormContext();
    const fieldState = getFieldState(name, formState);

    const inputRef = useRef(null);
    const { locale } = useLocale();
    const { dir } = useLanguage();

    const state = useNumberFieldState({ locale, formatOptions: convertedQtyFormatterConfig });
    const { labelProps, groupProps, inputProps } = useNumberField({ label }, state, inputRef);

    const value = watch(name);

    useDidUpdate(() => {
        if (value !== state.numberValue && !isNaN(state.numberValue)) {
            setValue(name, state.numberValue, { shouldValidate: true, shouldDirty: true });
        } else if (isNaN(state.numberValue)) {
            setValue(name, null);
        }
    }, [state.numberValue]);

    useEffect(() => {
        if (value !== state.numberValue) {
            state.setNumberValue(value);
        }
    }, [value]);

    return (
        <div className="w-full gap-1 flex flex-col">
            {label && (
                <label {...labelProps} className="block text-sm font-medium text-brand-text">
                    {label}
                </label>
            )}
            <div {...groupProps} className="relative flex">
                {currency && (
                    <div
                        className={cn(
                            'flex justify-center items-center flex-1 basis-0 text-sm text-brand-text bg-brand-background-dark border-brand-neutral-700',
                            {
                                'text-brand-neutral-400 cursor-not-allowed': disabled,
                                'pr-3 border border-l-0 rounded-r-md': dir === 'rtl',
                                'pl-3 border border-r-0 rounded-l-md': dir === 'ltr'
                            }
                        )}>
                        <PortalCurrencyIcon show={currency} disabled={disabled} />
                    </div>
                )}
                <input
                    {...inputProps}
                    ref={mergeRefs(inputRef, ref)}
                    value={state.inputValue}
                    placeholder={props.placeholder}
                    onKeyDown={(e) => {
                        if (step >= 100_000 && ['k', 'm'].includes(e.key)) {
                            e.preventDefault();
                            const val = parseShortFormNumber(state.numberValue, e.key);
                            if (!isNaN(val)) {
                                setValue(name, val, { shouldValidate: true, shouldDirty: true });
                                state.commit();
                            }
                        }
                    }}
                    autoComplete="off"
                    className={cn(
                        'appearance-none block w-full p-2 bg-brand-background-dark border border-brand-neutral-700 text-sm placeholder-brand-neutral-500 focus:outline-none',
                        {
                            'text-brand-text': !className,
                            'text-brand-neutral-400 cursor-not-allowed': disabled,
                            'pr-1.5 pl-3 border-r-0 rounded-l-md': dir === 'rtl',
                            'pl-1.5 pr-3 border-l-0 rounded-r-md': dir === 'ltr'
                        },
                        className
                    )}
                    disabled={disabled}
                />
                <div
                    className={cn('absolute flex flex-col h-full justify-center text-xs text-brand-text w-5', {
                        'right-2': dir === 'ltr',
                        'left-2': dir === 'rtl'
                    })}>
                    <button
                        type="button"
                        className={cn('px-0', {
                            'text-brand-neutral-400 cursor-not-allowed': disabled,
                            'hover:text-brand-green-light': !disabled
                        })}
                        onClick={() => {
                            if (!disabled) {
                                const prevValue = +(getValues(name) || 0);
                                const nextValue = BigNumber(prevValue).plus(step).toNumber();
                                // state.setNumberValue(nextValue);
                                setValue(name, nextValue, { shouldValidate: true, shouldDirty: true });
                            }
                        }}>
                        &#x25B2;
                    </button>
                    <button
                        type="button"
                        className={cn('px-0', {
                            'text-brand-neutral-400 cursor-not-allowed': disabled,
                            'hover:text-brand-green-light': !disabled
                        })}
                        onClick={() => {
                            if (!disabled) {
                                const prevValue = +(getValues(name) || 0);
                                const nextValue = BigNumber(prevValue).minus(step).toNumber();
                                if (nextValue <= +min) setValue(name, min, { shouldValidate: true });
                                // if (nextValue <= +min) state.setNumberValue(+min);
                                // else state.setNumberValue(nextValue);
                                else setValue(name, nextValue, { shouldValidate: true, shouldDirty: true });
                            }
                        }}>
                        &#x25BC;
                    </button>
                </div>
            </div>
            {fieldState.error ? <span className="text-brand-red italic">{fieldState.error?.message}</span> : null}
        </div>
    );
});

RHFNumberInput.displayName = 'RHFNumberInput';

export default RHFNumberInput;
